import { useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { CompleteProfileResponse, ErrorObject } from '@msc-b2c/interfaces/interfaces';
import {
  ApiErrorModalProps,
  ConfirmationProps,
  MSCVoyagersClubStepProps,
  RegisterProps,
  WarningProps,
} from '@msc-b2c/interfaces/types';
import { Button, getFormChildren, Text } from '@msc-b2c/shared-components';
import {
  closeLoader,
  CompleteProfileMyMSC,
  createNewSession,
  decodeHTMLEntities,
  getActiveSession,
  openLoader,
  setDeepValue,
  trackEvent,
} from '@msc-b2c/shared-utils';
import { getErrorContent, retrieveWarningByErrorCode } from '../utils/apiUtils';
import { usePrivateAreaPageViewTracking } from '../utils/analyticsUtils';
import { ErrorModal } from '../errormodal/ErrorModal';
import { BookingNumberSection } from './bookingnumbersection/BookingNumberSection';
import MSCVoyagersClubStep from './voyagersClubStep/MSCVoyagersClubStep';
import Confirmation from './confirmation/Confirmation';
import styles from './register.module.scss';

const getTrackedErrorMessage = (title: string, message: string): string =>
  [decodeHTMLEntities(title), decodeHTMLEntities(message)].filter(Boolean).join(' | ');

export function Register(registrationProps: Readonly<RegisterProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [error, setError] = useState<ApiErrorModalProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [registrationWarnings, setRegistrationWarnings] = useState<WarningProps[]>([]);
  const { email } = getActiveSession();

  usePrivateAreaPageViewTracking();

  useEffect(() => {
    isLoading ? openLoader('.loader') : closeLoader('.loader');
  }, [isLoading]);

  const {
    id,
    submitLabelPlaceholder,
    enableCheckboxTitleAndSubtitle = false,
    registrationFormTitle,
    registrationFormTitleStyle,
    registrationFormTitlePosition,
    registrationFormDescription,
    formInputFields,
    formInputFieldsCheckBox,
    enabledBookingNumber,
    labelToggleBookingNumber,
    inputLabelBookingNumber,
    inputPlaceholderBookingNumber,
    validationRulesContainerBookingNumber,
    defaultToggleOptions,
    toggleOneVisibility,
    mscVoyagersClubMemberLabel,
    mscVoyagersClubMemberInputLabel,
    mscVoyagersClubMemberInputPlaceholder,
    validationRulesMscVoyagersClubNumber,
    joinMscVoyagersClubLabel,
    joinMscVoyagersClubSubtitle,
    bulletPointIcon,
    bulletPointsForMscVoyagersClub,
    policyDescription,
    checkIconImgReference,
    checkIconImgAltText,
    confirmationTitle,
    confirmationDescription,
    myMscLinkLabel,
    myMscLink,
    checkIconImgDMURL,
    ctaLabel,
    closingIcon,
    errors,
    warnings,
    autoEnroll,
  } = registrationProps;

  const voyagerClubProps: MSCVoyagersClubStepProps = {
    defaultToggleOptions,
    toggleOneVisibility,
    mscVoyagersClubMemberLabel,
    mscVoyagersClubMemberInputLabel,
    mscVoyagersClubMemberInputPlaceholder,
    validationRulesMscVoyagersClubNumber,
    joinMscVoyagersClubLabel,
    joinMscVoyagersClubSubtitle,
    bulletPointIcon,
    bulletPointsForMscVoyagersClub,
    policyDescription,
  };

  const confirmationProps: ConfirmationProps = {
    checkIconImgReference,
    checkIconImgAltText,
    confirmationTitle,
    confirmationDescription,
    myMscLinkLabel,
    myMscLink,
    checkIconImgDMURL,
  };

  const formMethods = useForm({
    shouldFocusError: false,
  });

  const toggleOpenModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const onSubmitForm = async (data: { [key: string]: any }) => {
    setIsLoading(true);
    const promises: Promise<void>[] = [];
    delete data.bookingNumberToggle;
    if (!data.hasMembership) {
      delete data.membership;
    }
    delete data.hasMembership;
    delete data.prefix;
    if (!data.mobilePhone || data.mobilePhone.trim() === '') {
      delete data.mobilePhone;
    }
    if (data?.booking?.number === '') {
      delete data.booking;
    }

    if (autoEnroll) {
      data.createNewMembership = true;
    }

    const body = {
      ...data,
      email: email,
      ...((data.createNewMembership || data?.membership?.number || data.marketingConsent) && {
        marketingConsents: {
          ...((data.createNewMembership || data?.membership?.number) && {
            loyaltyClubEmail: true,
          }),
          email: !!data.marketingConsent,
          mobilePhone: !!(data.marketingConsent && data.mobilePhone),
        },
      }),
      profilingConsents: {
        email: !!data.profilingConsents,
        mobilePhone: !!(data.profilingConsents && data.mobilePhone),
      },
    };

    delete body.marketingConsent;

    Object.entries(body).forEach(([key, value]) => {
      promises.push(setDeepValue(body, key, value as FormDataEntryValue));
    });

    await Promise.all(promises);

    const completeProfileData = new CompleteProfileMyMSC(false, false, body, '');

    completeProfileData
      .sendData()
      .then((response: CompleteProfileResponse) => {
        setIsLoading(false);
        createNewSession(
          response.token,
          response.loginMode,
          'mymsc',
          email,
          response.name,
          response.surname,
          '',
          '',
          response.crmasisId,
          response.goldenId,
          ''
        );

        if (response?._warnings && warnings) {
          setRegistrationWarnings(retrieveWarningByErrorCode(response?._warnings, warnings));
        }
        setConfirmation(true);

        globalThis.didomiOnReady = globalThis.didomiOnReady || [];
        globalThis.didomiOnReady.push(() => {
          const eventData = {
            event: 'account_creation',
            member_already: data.membership?.number ? 'yes' : 'no',
            voyagers_club_opt_in: data.createNewMembership ? 'yes' : 'no',
            third_party_data_opt_in: data.thirdPartyConsent || data.profilingConsent ? 'yes' : 'no',
            marketing_opt_in: data.marketingConsent ? 'yes' : 'no',
          };
          globalThis.adobeDataLayer?.push(eventData);
        });
      })
      .catch((error: ErrorObject) => {
        setIsLoading(false);
        console.error('Error:', error);
        const errorCode = error?.issue_code ?? 'unknown_error';
        const { title, message } = getErrorContent(error, errors);
        trackEvent(
          'error',
          {
            error_source: 'Mulesoft',
            error_message: getTrackedErrorMessage(title, message),
            error_code: errorCode,
          },
          undefined,
          true
        );
        setError({ message, title, iconType: errors?.errorMessageIcon ?? '' });
        toggleOpenModal();
      });
  };

  return (
    <>
      {!confirmation && (
        <div className={styles['formContainer']}>
          <div style={{ width: '100%', textAlign: registrationFormTitlePosition }}>
            <Text
              text={registrationFormTitle}
              tagHtml="p"
              styleOption={registrationFormTitleStyle}
            />
          </div>
          <div
            className="cmp-register-wrapper"
            id={id}
          >
            <form
              className="cmp-form cmp-register"
              onSubmit={formMethods.handleSubmit(onSubmitForm as SubmitHandler<FieldValues>)}
            >
              <div className={styles['formDescription']}>
                <Text
                  text={registrationFormDescription}
                  styleOption="cmp-text__big text__description"
                />
              </div>
              {formInputFields?.childInputs &&
                getFormChildren(formInputFields.childInputs, formMethods)}
              <BookingNumberSection
                enabledBookingNumber={enabledBookingNumber}
                labelToggleBookingNumber={labelToggleBookingNumber}
                inputLabelBookingNumber={inputLabelBookingNumber}
                inputPlaceholderBookingNumber={inputPlaceholderBookingNumber}
                validationRulesContainerBookingNumber={validationRulesContainerBookingNumber}
                register={formMethods.register}
                errors={formMethods.formState.errors}
                clearErrors={formMethods.clearErrors}
                trigger={formMethods.trigger}
                setValue={formMethods.setValue}
              />
              {!autoEnroll && (
                <MSCVoyagersClubStep
                  {...voyagerClubProps}
                  register={formMethods.register}
                  trigger={formMethods.trigger}
                  errors={formMethods.formState.errors}
                  clearErrors={formMethods.clearErrors}
                  setValue={formMethods.setValue}
                />
              )}
              {formInputFieldsCheckBox?.childInputs && (
                <div className="cmp-form-options cmp-form-options--checkbox-container">
                  {getFormChildren(formInputFieldsCheckBox.childInputs, formMethods, {
                    enableCheckboxTitleAndSubtitle,
                  })}
                </div>
              )}
              <div className="cmp-register__cta-section">
                <Button
                  styleOption="cmp-button__primary -extended"
                  buttonType="submit"
                  disabled={isLoading}
                  text={submitLabelPlaceholder}
                />
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmation && (
        <div className={styles['confirmationOuterContainer']}>
          <Confirmation
            {...confirmationProps}
            warningsArray={registrationWarnings}
          />
        </div>
      )}
      {isModalOpen && (error?.message || error?.title) && (
        <ErrorModal
          title={error.title}
          message={error.message}
          styleOption={error.styleOption}
          iconType={error.iconType}
          closingIcon={closingIcon}
          ctaLabel={ctaLabel}
          handleClosure={toggleOpenModal}
          handleClick={toggleOpenModal}
        />
      )}
    </>
  );
}
